import { useFragment } from '@apollo/client';
import { Alert, Box, Button, Stack, styled } from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import {
  memo,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useResizable } from 'react-resizable-layout';
import {
  BlockContainer,
  BlockContent,
  BlockHeader,
  CollapsingLayoutBlock,
  DynamicIcon,
  EntityOpenModeSwitcher,
  SidePanelTabs,
  Tooltip,
} from 'src/components';
import { ClientMenu } from 'src/components/ClientComponents/client-menu';
import { ClientName } from 'src/components/ClientComponents/client-name';
import { ExpandedConversations } from 'src/components/EntityFullPageTabs/expanded-conversations';
import { ExpandedSites } from 'src/components/EntityFullPageTabs/expanded-sites';
import {
  FULL_PAGE_CONTENT_COLUMN_SX,
  getEntityDetailsRailSx,
  getPanelToolsSlotSx,
  RIGHT_PANEL_RESIZE_GUTTER_WIDTH,
  SIDE_PANEL_TABS_FULL_PAGE_INNER_STYLES,
} from 'src/components/EntitySidePanel';
import { DrawerCloseControl } from 'src/components/Navbar/DesktopDrawer';
import { ResizeHandle } from 'src/components/ResizeHandle';
import { ClientTabs } from 'src/components/SidePanelTabs/ClientTab/client-const';
import { useClientTabs } from 'src/components/SidePanelTabs/ClientTab/use-client-tabs';
import { ViewerListStateful } from 'src/components/ViewerListStateful/viewer-list-stateful';
import { gql } from 'src/gql';
import { isClientDeleted, useGroupRules, useRestoreClient, UserGroupRules } from 'src/graphql';
import { Client } from 'src/graphql/entities/client/client.types';
import { RecordOpenMode } from 'src/graphql/entities/user-setting-value/user-setting-value.types';
import { EntityNames } from 'src/graphql/types';
import {
  RIGHT_PANEL_MAX_WIDTH_PX,
  RIGHT_PANEL_MIN_WIDTH_PX,
  RIGHT_PANEL_WIDTH_PX,
} from 'src/pages/Conversations/conversation';
import { GridBreadcrumbItem } from 'src/pages/CustomViews/custom-views-selected-view/base-selected-view';
import useClientViewsStore from 'src/store/client-views';
import { Modals, useModalMethods } from 'src/store/modals';
import { useCopyToClipboard, useCustomView } from 'src/utils/hooks';
import { AppEvents, emitAppEvent, subscribeToAppEvent } from 'src/utils/hooks/use-app-events';
import { TabType, useGlobalTabs } from 'src/utils/hooks/use-global-tabs';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';
import { ClientActivityView } from './client-activity-view/client-activity-view';
import { ClientSidePanel } from './SidePanel/side-panel';

const OPEN_DETAILS_PANEL_TOOLTIP = 'Open Client Details';
const CLOSE_DETAILS_PANEL_TOOLTIP = 'Close Client Details';

const EXPANDED_SITES_VIEW_ID = 'clientSites';

const ClientContainer = styled(Stack, {
  name: 'ClientContainer',
})(({ theme }) => ({
  position: 'relative',
  flexDirection: 'row',
  flex: 1,
  zIndex: theme.zIndex.app.ServiceDeskConversation,
  width: 0,
}));

const ClientDetailsWrapper = styled(CollapsingLayoutBlock, {
  name: 'ClientDetailsWrapper',
})(({ theme, open }) => ({
  position: 'static',
  [theme.breakpoints.down('sm')]: {
    position: 'absolute',
    top: 0,
    height: '100%',
    right: -1,
    ...(!open ? {} : { right: 1 }),
  },
}));

const SidePanelTabsInnerStyles = {
  padding: 0,
};

const SidePanelTabStyles = {
  fontSize: '0.75rem',
};

const TabContentFragment = gql(`
  fragment ClientWindowTabContent_Client on Client {
    id
    clientId
    partnerId
    deletedAt
  }
`);

interface TabContentProps {
  clientId: string;
  activeTab: ClientTabs;
}

const TabContent = ({ activeTab, clientId }: TabContentProps) => {
  const { data: client, complete } = useFragment({
    fragment: TabContentFragment,
    fragmentName: 'ClientWindowTabContent_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  const { showModal: showUpdateClientToAddressModal } = useModalMethods(
    Modals.UpdateClientToAddress,
  );

  const conversationsViewFilters = useMemo(
    () => [
      {
        attribute: 'requester/participantIdentity',
        operator: '=',
        value: clientId,
        logicalOperator: 'or',
      },
      {
        attribute: 'associatedAddresses/clients/clientId',
        operator: '=',
        value: clientId,
        logicalOperator: 'or',
      },
    ],
    [clientId],
  );

  const sitesViewFilters = useMemo(
    () => [
      {
        attribute: 'primaryClientId',
        operator: '=',
        value: clientId,
        logicalOperator: 'or',
      },
      {
        attribute: 'clients/clientId',
        operator: '=',
        value: clientId,
        logicalOperator: 'or',
      },
    ],
    [clientId],
  );

  useEffect(
    () =>
      subscribeToAppEvent(AppEvents.ClientsToAddressAssociation, ({ clientIds }) => {
        if (!clientId || !clientIds.includes(clientId)) return;
        emitAppEvent(AppEvents.SearchRecordsInvalidated, {
          entityName: EntityNames.Site,
          viewId: EXPANDED_SITES_VIEW_ID,
        });
      }),
    [clientId],
  );

  // Scopes the "Remove client from sites" bulk action to this page: it is offered only where a
  // client is in scope, and takes its client id from here rather than from a picker.
  const isDeleted = isClientDeleted(client);
  const bulkActionContextValues = useMemo(
    () => (isDeleted ? undefined : { removeClientId: clientId }),
    [isDeleted, clientId],
  );

  const onAddSiteClicked = useCallback(() => {
    showUpdateClientToAddressModal({
      // TODO(gql-migrate): UpdateClientToAddressModal still consumes the legacy Client
      // shape; it only reads clientId/partnerId, which this fragment selects.
      // TODO(gql-migrate): ^^ it is wrong, UpdateClientToAddressModal passes entire Client object down. Investigate this.
      client: client as Client,
    });
  }, [client, showUpdateClientToAddressModal]);

  if (!complete) {
    return null;
  }

  switch (true) {
    case activeTab === ClientTabs.Sites:
      return (
        <ExpandedSites
          viewFilters={sitesViewFilters}
          customViewType={EntityNames.Client}
          viewId={EXPANDED_SITES_VIEW_ID}
          bulkActionContextValues={bulkActionContextValues}
          createButton={
            <Button onClick={onAddSiteClicked} disabled={isClientDeleted(client)}>
              + Add Site
            </Button>
          }
        />
      );

    case activeTab === ClientTabs.Conversations:
      return (
        <ExpandedConversations
          viewFilters={conversationsViewFilters}
          customViewType={EntityNames.Client}
          viewId="clientConversations"
        />
      );

    case activeTab === ClientTabs.Activity:
      return <ClientActivityView clientId={clientId} partnerId={client.partnerId} />;

    default:
      return null;
  }
};

const ClientWindowFragment = gql(`
  fragment ClientWindow_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    deletedAt
    ...ClientWindowTabContent_Client
    ...ClientSidePanel_Client
    ...ClientMenu_Client
  }
`);

export const ClientWindowComponent = ({
  isSidePanelExpanded,
  clientId,
  onCloseClient,
  modeSwitcher,
  activeTab: activeTabProp,
  onActiveTabChange,
}: {
  isSidePanelExpanded: boolean;
  clientId: string;
  onCloseClient: () => void;
  modeSwitcher?: React.ReactNode;
  /**
   * URL-driven tab, supplied by the full-page route only. Surfaces without a
   * `:entityTab` param (the Inbox preview pane) omit both props and keep using
   * the persisted store.
   */
  activeTab?: ClientTabs;
  onActiveTabChange?: (tab: ClientTabs) => void;
}) => {
  const { data: client, complete } = useFragment({
    fragment: ClientWindowFragment,
    fragmentName: 'ClientWindow_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });
  const { navigate } = useNavigateRoute();
  const { isMobile, isDesktop } = useScreenDimensions();
  const {
    selectedClientTab,
    setSelectedClientTab,
    clientSidePanelStateOpen,
    setClientSidePanelStateOpen,
  } = useClientViewsStore();

  const activeTab = activeTabProp ?? selectedClientTab;
  const setActiveTab = onActiveTabChange ?? setSelectedClientTab;

  const customView = useCustomView(EntityNames.Client);
  const customViewId = customView?.customViewId;

  useEffect(() => {
    if (isMobile) {
      setClientSidePanelStateOpen(false);
    }
  }, [isMobile, setClientSidePanelStateOpen]);

  const canResizeDetailsView = clientSidePanelStateOpen && isDesktop;

  const isResizableSplitMode = isSidePanelExpanded && canResizeDetailsView;

  const clientViewRef = useRef<HTMLDivElement | null>(null);

  const { closeTabWith } = useGlobalTabs();

  const closeTab = () => {
    closeTabWith((tab) => tab.type === TabType.Client && tab.entity?.id === clientId, false);
  };

  const modeSwitcherButton = modeSwitcher ?? (
    <EntityOpenModeSwitcher
      openMode={RecordOpenMode.SidePeek}
      onClick={() => {
        closeTab();
        navigate(
          portalPaths.entity.all({
            entityName: 'clients',
            id: clientId,
            customViewId,
          }),
        );
      }}
    />
  );

  const {
    position: sidePanelWidth,
    separatorProps,
    isDragging,
  } = useResizable({
    axis: 'x',
    initial: RIGHT_PANEL_WIDTH_PX,
    min: RIGHT_PANEL_MIN_WIDTH_PX,
    max: RIGHT_PANEL_MAX_WIDTH_PX,
    containerRef: clientViewRef as RefObject<HTMLElement>,
    reverse: true,
    disabled: !clientSidePanelStateOpen || isMobile,
  });

  const closeSidePanel = useCallback(
    () => setClientSidePanelStateOpen(false),
    [setClientSidePanelStateOpen],
  );

  const { copyToClipboard } = useCopyToClipboard();

  const { checkValidity } = useGroupRules();
  const { tabs } = useClientTabs();

  const { restoreClient } = useRestoreClient();

  const onRestoreClientClick = useCallback(async () => {
    await restoreClient(clientId);
  }, [restoreClient, clientId]);

  const [panelToolsSlot, setPanelToolsSlot] = useState<HTMLElement | null>(null);

  if (!complete) {
    return null;
  }

  return (
    <ClientContainer
      ref={clientViewRef}
      sx={{
        userSelect: isDragging ? 'none' : 'auto',
      }}
    >
      <BlockContainer sx={{ minWidth: '20rem' }}>
        <BlockHeader>
          <DrawerCloseControl />
          {!isMobile && isSidePanelExpanded ? (
            <GridBreadcrumbItem handleClick={onCloseClient} text={customView?.name || 'All'} />
          ) : null}
          <ClientName
            fullName={client.fullName}
            tooltipText="Click to copy"
            onClick={() => {
              if (client.fullName) {
                void copyToClipboard(client.fullName);
              }
            }}
          />
          <Box flexGrow={1} />
          <ViewerListStateful entity="Client" entityId={clientId} />
          {!isMobile && modeSwitcherButton}
          <ClientMenu clientId={clientId} />
          <Tooltip
            title={
              clientSidePanelStateOpen
                ? CLOSE_DETAILS_PANEL_TOOLTIP
                : OPEN_DETAILS_PANEL_TOOLTIP
            }
          >
            <DynamicIcon
              name={clientSidePanelStateOpen ? 'right_panel_close' : 'right_panel_open'}
              sx={{ cursor: 'pointer' }}
              onClick={() => setClientSidePanelStateOpen(!clientSidePanelStateOpen)}
            />
          </Tooltip>
        </BlockHeader>
        <BlockContent
          sx={{
            p: 0,
            minHeight: 0,
            position: 'static',
            overflow: 'visible',
          }}
        >
          {isSidePanelExpanded ? (
            // Full page: SidePanelTabs itself spans content + rail, so the tab strip
            // (and its bottom border) run the full container width, matching the
            // prototype's sd-tabs; content and rail form a row below it and each
            // scrolls independently, with the rail's cards running under the tabs band.
            <SidePanelTabs
              showLabel
              key={clientId}
              activeTab={activeTab}
              setActiveTab={(newValue: SetStateAction<ClientTabs>) => {
                setActiveTab(newValue as ClientTabs);
              }}
              tabs={tabs}
              variant="scrollable"
              tabSx={SidePanelTabStyles}
              innerSx={SIDE_PANEL_TABS_FULL_PAGE_INNER_STYLES}
              hasBorderTop={false}
            >
              <>
                <Stack minHeight={0} flexGrow={1} sx={FULL_PAGE_CONTENT_COLUMN_SX}>
                  <TabContent activeTab={activeTab} clientId={clientId} />
                  {isClientDeleted(client) ? (
                    <Alert
                      severity="info"
                      action={
                        checkValidity([UserGroupRules.AccessToRestoreClients]) ? (
                          <Button color="inherit" size="small" onClick={onRestoreClientClick}>
                            RESTORE
                          </Button>
                        ) : null
                      }
                    >
                      This client was deleted
                    </Alert>
                  ) : null}
                </Stack>

                {isResizableSplitMode ? (
                  <Box
                    sx={{
                      position: 'relative',
                      flexShrink: 0,
                      width: RIGHT_PANEL_RESIZE_GUTTER_WIDTH,
                    }}
                  >
                    <ResizeHandle
                      axis="x"
                      isDragging={isDragging}
                      {...separatorProps}
                      sx={{ left: 0, right: 0, top: 0 }}
                    />
                  </Box>
                ) : null}

                <ClientDetailsWrapper
                  open={clientSidePanelStateOpen}
                  sx={getEntityDetailsRailSx({
                    isMobile,
                    sidePanelWidth,
                    isDragging,
                    isOpen: clientSidePanelStateOpen,
                  })}
                >
                  <Box ref={setPanelToolsSlot} sx={getPanelToolsSlotSx(isMobile)} />
                  <Box
                    sx={{
                      pt: '0.5rem',
                      pr: isMobile ? 0 : '0.25rem',
                      pb: '0.5rem',
                    }}
                  >
                    <ClientSidePanel
                      onClose={closeSidePanel}
                      clientId={clientId}
                      extendedView
                      toolsSlot={panelToolsSlot}
                    />
                  </Box>
                </ClientDetailsWrapper>
              </>
            </SidePanelTabs>
          ) : (
            <>
              <Stack flexGrow={1} justifyContent="space-between" overflow="auto">
                <Box
                  sx={{
                    position: 'relative',
                    width:
                      !isMobile && clientSidePanelStateOpen
                        ? `calc(100% - ${Math.max(sidePanelWidth, 0)}px)`
                        : '100%',
                    height: '100%',
                  }}
                >
                  <SidePanelTabs
                    showLabel
                    key={clientId}
                    activeTab={activeTab}
                    setActiveTab={(newValue: SetStateAction<ClientTabs>) => {
                      setActiveTab(newValue as ClientTabs);
                    }}
                    tabs={tabs}
                    variant="scrollable"
                    tabSx={SidePanelTabStyles}
                    innerSx={SidePanelTabsInnerStyles}
                    hasBorderTop={false}
                  >
                    <Stack flexGrow={1} minHeight={0}>
                      <TabContent activeTab={activeTab} clientId={clientId} />
                    </Stack>
                  </SidePanelTabs>
                </Box>

                <ClientDetailsWrapper
                  open={clientSidePanelStateOpen}
                  sx={{
                    width: isMobile ? '100%' : sidePanelWidth,
                    ...(isDragging && { transition: 'none' }),
                    zIndex: isMobile ? 1 : undefined,
                  }}
                >
                  <ClientSidePanel onClose={closeSidePanel} clientId={clientId} extendedView />
                </ClientDetailsWrapper>
              </Stack>
              {isClientDeleted(client) ? (
                <Alert
                  severity="info"
                  action={
                    checkValidity([UserGroupRules.AccessToRestoreClients]) ? (
                      <Button color="inherit" size="small" onClick={onRestoreClientClick}>
                        RESTORE
                      </Button>
                    ) : null
                  }
                >
                  This client was deleted
                </Alert>
              ) : null}
            </>
          )}
        </BlockContent>
      </BlockContainer>
    </ClientContainer>
  );
};

export const ClientWindow = memo(ClientWindowComponent);
